import { useEffect, useRef } from 'react';
import SectionHeader from './SectionHeader';

const STUDENT_FEATURES = [
  'AI study plans',
  'Curated videos, no ads',
  'Interactive Q&A & quizzes',
  'Progress tracking',
];

const FAMILY_FEATURES = [
  'Everything in Student Plan',
  'Up to 4 student accounts',
  'Parent dashboard & oversight',
  'Personalized recommendations',
];

function PlanCard({ title, price, seats, features, badge, cardClass, borderClass, cta, gaCtaValue, onView }) {
  return (
    <div className={`rounded-2xl p-6 border ${cardClass} ${borderClass} flex flex-col`}>
      <div className="flex items-start justify-between mb-1">
        <div className="font-bold text-lg text-darkrock">{title}</div>
        {badge && (
          <span className="bg-pastelpurple text-darkrock/70 rounded px-2 py-0.5 text-xs font-semibold">
            {badge}
          </span>
        )}
      </div>
      <div className="text-3xl font-bold text-darkrock mb-1">{price}</div>
      <div className="text-sm text-darkrock/60 mb-4">{seats}</div>
      <ul className="space-y-1 mb-6 flex-1">
        {features.map((f) => (
          <li key={f} className="flex items-center gap-2 text-sm text-darkrock/70">
            <span className="text-primary-600">✓</span> {f}
          </li>
        ))}
      </ul>
      <a
        href="https://parent.learnforest.app/trial"
        data-ga-cta={gaCtaValue}
        className="bg-primary-600 hover:bg-primary-700 text-white font-semibold w-full py-3 rounded-xl text-center transition-colors block"
        onClick={onView}
      >
        {cta}
      </a>
    </div>
  );
}

function PricingSection() {
  const sectionRef = useRef(null);
  const viewedRef = useRef(false);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !viewedRef.current) {
          viewedRef.current = true;
          if (typeof window !== 'undefined' && window.__lfTrack) {
            window.__lfTrack('pricing_section_view', { surface: 'marketing_home' });
          }
        }
      },
      { threshold: 0.2 },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <section ref={sectionRef} data-ga-section="pricing" className="bg-pastelblue-light py-20">
      <div className="max-w-screen-lg mx-auto px-6">
        <SectionHeader
          eyebrow="Pricing"
          title="Simple, honest pricing."
          sub="First month free. Cancel anytime."
        />

        <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-6">
          <PlanCard
            title="Student Plan"
            price="$3.99/mo"
            seats="1 student account"
            features={STUDENT_FEATURES}
            cardClass="bg-white"
            borderClass="border-gray-200"
            cta="Start free trial"
            gaCtaValue="pricing_student"
          />
          <PlanCard
            title="Family Plan"
            price="$7.99/mo"
            seats="Up to 4 student accounts"
            features={FAMILY_FEATURES}
            badge="POPULAR"
            cardClass="bg-white iv1-soft-shadow"
            borderClass="border-2 border-pastelpurple"
            cta="Start free trial"
            gaCtaValue="pricing_family"
          />
        </div>

        <p className="text-center text-darkrock/50 text-sm mt-6">
          All plans include a 1-month free trial. No credit card required to start.
        </p>
      </div>
    </section>
  );
}

export default PricingSection;
